import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { UploadObsService } from "src/app/shared/services/upload-obs.service";
import AppShareHelper from "src/app/shared/utils/helper";

@Component({
  selector: "app-tab-animal-and-pois",
  templateUrl: "./tab-animal-and-pois.component.html",
  styleUrls: ["./tab-animal-and-pois.component.scss"],
})
export class TabAnimalAndPoisComponent implements OnInit, OnChanges {
  @Input() model: any = {};
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;
  @Input() uploadProgress: { [key: string]: number } = {};
  @Input() uploadStates: { [key: string]: boolean } = {};

  @Output() modelChange = new EventEmitter<any>();
  @Output() autoCheckEvent = new EventEmitter<string>();

  animalPOIFile: File;

  // Tracking for changes
  originalModel: any = {};

  constructor(
    private toastr: ToastrService,
    private uploadService: UploadObsService
  ) {}

  ngOnInit() {
    this.initializeAnimalData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["model"] && changes["model"].currentValue) {
      this.initializeAnimalData();
    }
  }

  private initializeAnimalData() {
    // Initialize tracking
    this.originalModel = AppShareHelper.deepClone(this.model);
  }

  async saveAnimal(): Promise<boolean> {
    try {
      // Update model and emit change
      this.modelChange.emit(this.model);

      // Reset tracking
      this.originalModel = AppShareHelper.deepClone(this.model);

      return true;
    } catch (error) {
      console.error("Error saving animal data:", error);
      this.toastr.error("Failed to save animal changes");
      return false;
    }
  }

  validateAnimal(): boolean {
    // Check if animal POI file is required when displayAnimalPOI is true
    if (this.model.displayAnimalPOI && !this.model.animalPOI) {
      this.toastr.error("Please complete all required fields for Animal and pois");
      return false;
    }

    return true;
  }

  autoCheck(field: string) {
    this.autoCheckEvent.emit(field);
  }

  uploadAnimalPOIFile(e: File[]) {
    const validPDFType = ["application/pdf"];
    if (!this.isFileSizeValid(e[0])) {
      this.toastr.error("Max file size exceeds 50MB", "Error");
      return;
    }
    this.animalPOIFile = e[0];
    if (validPDFType.includes(this.animalPOIFile.type)) {
      this.model.animalPOIFileName = this.animalPOIFile.name;
      const uploadKey = this.generateUploadKey("animal_poi", this.animalPOIFile.name);
      this.setUploadState(uploadKey, true);
      this.model.animalPOIUploadKey = uploadKey;
      this.model.animalPOI = null;
      this.uploadService.uploadFileToS3("digital/files", this.animalPOIFile).subscribe(
        (response: any) => {
          if (response && response.type === "progress") {
            this.setUploadProgress(uploadKey, response.percent);
            return;
          }

          if (response && response.type === "complete") {
            this.model.animalPOI = response.url;
            this.setUploadState(uploadKey, false);
            this.toastr.success(`File ${this.animalPOIFile.name} uploaded successfully`);
            this.model.animalPOIUploadKey = null;
            this.cleanupUpload(uploadKey);
            this.modelChange.emit(this.model);
          }
        },
        error => {
          this.setUploadState(uploadKey, false);
          this.toastr.error(`Failed to upload: ${error.message || error}`);
          console.error("Upload error:", error);

          this.model.animalPOIUploadKey = null;
          this.cleanupUpload(uploadKey);
        }
      );
    }
  }

  removeAnimalPOI() {
    this.model.animalPOI = null;
    this.model.animalPOIFileName = null;
    this.modelChange.emit(this.model);
  }

  // Helper methods from parent component
  isFileSizeValid(file: File): boolean {
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    return file.size <= maxSize;
  }

  generateUploadKey(prefix: string, fileName: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `${prefix}_${timestamp}_${random}_${fileName}`;
  }

  setUploadState(key: string, isUploading: boolean) {
    this.uploadStates[key] = isUploading;
  }

  setUploadProgress(key: string, progress: number) {
    this.uploadProgress[key] = progress;
  }

  cleanupUpload(key: string) {
    delete this.uploadProgress[key];
    delete this.uploadStates[key];
  }

  isUploadInProgress(key: string): boolean {
    return this.uploadStates[key] === true;
  }

  getProgressByKey(key: string): number {
    return this.uploadProgress[key] || 0;
  }
}
